import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { OwnerButton, OwnerCard, OwnerInfoList, OwnerShell } from '../OwnerShell';
import { SelectField, TextAreaField, TextField } from '../OwnerFields';
import { UploadTile } from '../UploadTile';
import { StatusBadge } from '../../common/StatusBadge';
import { useMyVehicle } from '../../../hooks/useMyVehicles';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { ownerProfile } from '../../../data/ownerProfile';
import { requestTone } from '../../../utils/ownerStatus';
import { toBn } from '../../../utils/bn';
import type { OwnerNav } from '../../../types/owner';
const trackSteps = ['আবেদন জমা', 'ডিএমপি পর্যালোচনা', 'BRTA হস্তান্তর প্রমাণ যাচাই', 'ডিএমপি রেকর্ড হালনাগাদ'];
export function OwnerOwnership({
  nav
}: {
  nav: OwnerNav;
}) {
  const mv = useMyVehicle(nav.vehicleId);
  const {
    requests,
    submitRequest
  } = useOwnerService();
  const [type, setType] = useState('বিক্রয়সূত্রে হস্তান্তর');
  const [newOwner, setNewOwner] = useState({
    name: 'মোঃ জাহাঙ্গীর আলম',
    mobile: '০১৭৯৯৮৮৭৭৬৬',
    nid: '১৯৮৫৪৪৩৩২২১১০'
  });
  const [remarks, setRemarks] = useState('গাড়িটি বিক্রয় করা হয়েছে। BRTA-তে মালিকানা হস্তান্তরের আবেদন জমা দেওয়া হয়েছে।');
  const [docs, setDocs] = useState({
    deed: false,
    brta: false
  });
  const [error, setError] = useState('');
  if (!mv) return null;
  const v = mv.vehicle;
  const existing = requests.find(r => r.kind === 'মালিকানা পরিবর্তন' && r.vehicleId === v.id);
  const submit = () => {
    if (!docs.deed || !docs.brta) return setError('উভয় সহায়ক ডকুমেন্ট আপলোড করুন');
    submitRequest({
      kind: 'মালিকানা পরিবর্তন',
      ownerName: ownerProfile.name,
      vehicleReg: v.reg,
      subject: type,
      current: ownerProfile.name,
      proposed: `${newOwner.name} (${newOwner.mobile})`,
      reason: remarks,
      vehicleId: v.id,
      attachments: 2
    });
    nav.showToast('মালিকানা পরিবর্তনের আবেদন জমা হয়েছে');
  };
  const stepIdx = existing ? existing.status === 'অনুমোদিত' ? 4 : existing.status === 'পর্যালোচনাধীন' ? 2 : 1 : 0;
  return <OwnerShell nav={nav} title="মালিকানা পরিবর্তন সংক্রান্ত আবেদন" footer={!existing ? <OwnerButton onClick={submit} data-id="element-1826" data-mp="2213fqanhx459">আবেদন জমা দিন</OwnerButton> : undefined} data-id="element-1825" data-mp="26z4wgoxg8rdk">
      <p className="mb-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900" data-id="element-1827" data-mp="2gr28fxwa7ndc">
        <Info className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1828" data-mp="2bs7rz2lseawe" />এটি কেবল ডিএমপি রেকর্ড হালনাগাদের প্রশাসনিক আবেদন। এর মাধ্যমে BRTA-তে আইনগত মালিকানা হস্তান্তর সম্পন্ন হয় না।
      </p>
      <OwnerCard title="গাড়ির তথ্য" data-id="element-1829" data-mp="1j3tqgcvhowg3">
        <OwnerInfoList rows={[['গাড়ির নম্বর', v.reg], ['ধরন', v.typeLabel], ['চেসিস', <span key="c" className="font-mono text-[11.5px]" data-id="element-1831" data-mp="1x3l246yja0qx">{v.chassis}</span>], ['বর্তমান মালিক', ownerProfile.name]]} data-id="element-1830" data-mp="1j4v8j1axaj7b" />
      </OwnerCard>

      {existing ? <OwnerCard title="আবেদনের অবস্থা" className="mt-3" action={<StatusBadge tone={requestTone(existing.status)} className="!text-[10.5px]" data-id="element-1833" data-mp="38g5glorahhv9">{existing.status}</StatusBadge>} data-id="element-1832" data-mp="38hoy1ioolyym">
          <p className="mb-3 font-mono text-[11px] text-slate-500" data-id="element-1834" data-mp="34ipqrrfh9q40">{existing.id} • {existing.date}</p>
          <ol className="space-y-2.5" data-id="element-1835" data-mp="3gyz9q5k1l9ox">
            {trackSteps.map((s, i) => <li key={s} className="flex items-center gap-2.5 text-[13px]" data-id="element-1836" data-mp="3gww8epdfvu4w" data-mpi={i}>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${i < stepIdx ? 'bg-bdgreen-600 text-white' : i === stepIdx ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-1837" data-mp="3239int0026dc">{toBn(i + 1)}</span>
                <span className={i <= stepIdx ? 'font-medium text-navy-900' : 'text-slate-400'} data-id="element-1838" data-mp="3jt5z4uxgp4r">{s}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-[12px] text-slate-600" data-id="element-1839" data-mp="3l0186k64xhpu">প্রস্তাবিত মালিক: <b data-id="element-1840" data-mp="3pyvsxrnnxgcl">{existing.proposed}</b></p>
        </OwnerCard> : <div className="mt-3 space-y-3.5" data-id="element-1841" data-mp="hju9ks2kuanx">
          <SelectField id="o-type" label="পরিবর্তনের ধরন" required value={type} options={['বিক্রয়সূত্রে হস্তান্তর', 'উত্তরাধিকার সূত্রে', 'যৌথ মালিকানায় পরিবর্তন', 'মালিকের নাম সংশোধন']} onChange={setType} data-id="element-1842" data-mp="hj2av8donwtf" />
          <TextField id="o-name" label="প্রস্তাবিত মালিকের নাম" required value={newOwner.name} onChange={x => setNewOwner(s => ({
        ...s,
        name: x
      }))} data-id="element-1843" data-mp="2ut7x0nqoe4qc" />
          <div className="grid grid-cols-2 gap-3" data-id="element-1844" data-mp="2kt788ma1ewer">
            <TextField id="o-mob" label="মোবাইল" value={newOwner.mobile} onChange={x => setNewOwner(s => ({
          ...s,
          mobile: x
        }))} data-id="element-1845" data-mp="2ktpfedy8mv1g" />
            <TextField id="o-nid" label="NID" value={newOwner.nid} onChange={x => setNewOwner(s => ({
          ...s,
          nid: x
        }))} data-id="element-1846" data-mp="3di4q4oac6r6z" />
          </div>
          <UploadTile label="বিক্রয় দলিল / উত্তরাধিকার সনদ" required state={docs.deed ? 'uploaded' : 'missing'} onUpload={() => setDocs(d => ({
        ...d,
        deed: true
      }))} data-id="element-1847" data-mp="38k30tlmbhvn2" />
          <UploadTile label="BRTA হস্তান্তর আবেদনের রসিদ" required state={docs.brta ? 'uploaded' : 'missing'} onUpload={() => setDocs(d => ({
        ...d,
        brta: true
      }))} data-id="element-1848" data-mp="2ytrtrq7c76by" />
          <TextAreaField id="o-rem" label="মন্তব্য" value={remarks} onChange={setRemarks} data-id="element-1849" data-mp="5b1lszli2w6" />
          {error && <p className="text-[12px] font-medium text-red-600" data-id="element-1850" data-mp="3rg2tkyn4d37p">{error}</p>}
        </div>}
    </OwnerShell>;
}